import { useState, useMemo } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    Check,
    Globe,
    Calendar
} from 'lucide-react';
import { TIMEZONES } from '../utils/timezones';

export interface AvailabilitySlot {
    start_time_utc: string;
    end_time_utc: string;
    start_time_local: string;
    end_time_local: string;
    display?: string;
}

export interface AvailabilityDay {
    date: string;
    day_name: string;
    formatted_date?: string;
    slots: AvailabilitySlot[];
}

export interface SelectedSlot extends AvailabilitySlot {
    date: string;
    day_name: string;
}

export interface DateTimeSelectorProps {
    availableDays: AvailabilityDay[];
    selectedDate: string | null;
    onDateChange: (date: string) => void;
    selectedSlots: SelectedSlot[];
    onSlotToggle: (slot: AvailabilitySlot, date: string, dayName: string) => void;
    multiSelect?: boolean;
    maxSelections?: number;
    timezone: string;
    onTimezoneChange: (tz: string) => void;
    showTimezoneSelector?: boolean;
    ownerName?: string;
}

const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

export function DateTimeSelector({
    availableDays,
    selectedDate,
    onDateChange,
    selectedSlots,
    onSlotToggle,
    multiSelect = false,
    maxSelections,
    timezone,
    onTimezoneChange,
    showTimezoneSelector = true,
    ownerName
}: DateTimeSelectorProps) {
    // Get the current month to display - start with the first available day or today
    const initialMonth = useMemo(() => {
        if (availableDays.length > 0) {
            const firstDate = new Date(availableDays[0].date + 'T00:00:00');
            return { year: firstDate.getFullYear(), month: firstDate.getMonth() };
        }
        const today = new Date();
        return { year: today.getFullYear(), month: today.getMonth() };
    }, [availableDays]);

    const [viewMonth, setViewMonth] = useState(initialMonth);

    // Build a set of available dates for quick lookup
    const availableDatesSet = useMemo(() => {
        return new Set(availableDays.filter(d => d.slots.length > 0).map(d => d.date));
    }, [availableDays]);

    // Get the range of available dates
    const dateRange = useMemo(() => {
        if (availableDays.length === 0) return { min: null, max: null };
        const dates = availableDays.map(d => d.date).sort();
        return { min: dates[0], max: dates[dates.length - 1] };
    }, [availableDays]);

    // Generate calendar grid for current month
    const calendarDays = useMemo(() => {
        const { year, month } = viewMonth;
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const startDayOfWeek = firstDayOfMonth.getDay();
        const daysInMonth = lastDayOfMonth.getDate();

        const days: { date: string; day: number; isCurrentMonth: boolean; isToday: boolean }[] = [];

        // Add previous month's days
        const prevMonth = new Date(year, month, 0);
        const prevMonthDays = prevMonth.getDate();
        for (let i = startDayOfWeek - 1; i >= 0; i--) {
            const day = prevMonthDays - i;
            const prevYear = month === 0 ? year - 1 : year;
            const prevMonthNum = month === 0 ? 11 : month - 1;
            const dateStr = `${prevYear}-${String(prevMonthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: false, isToday: false });
        }

        // Add current month's days
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: true, isToday: dateStr === todayStr });
        }

        // Add next month's days to complete the grid (6 rows)
        const remainingDays = 42 - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            const nextYear = month === 11 ? year + 1 : year;
            const nextMonthNum = month === 11 ? 0 : month + 1;
            const dateStr = `${nextYear}-${String(nextMonthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            days.push({ date: dateStr, day, isCurrentMonth: false, isToday: false });
        }

        return days;
    }, [viewMonth]);

    // Get slots for the selected date
    const slotsForSelectedDate = useMemo(() => {
        if (!selectedDate) return [];
        const day = availableDays.find(d => d.date === selectedDate);
        return day?.slots || [];
    }, [selectedDate, availableDays]);

    // Get the day info for selected date
    const selectedDayInfo = useMemo(() => {
        if (!selectedDate) return null;
        return availableDays.find(d => d.date === selectedDate);
    }, [selectedDate, availableDays]);

    // Check if a slot is selected
    const isSlotSelected = (slot: AvailabilitySlot) => {
        return selectedSlots.some(s => s.start_time_utc === slot.start_time_utc);
    };

    // Navigate months
    const goToPrevMonth = () => {
        setViewMonth(prev => {
            if (prev.month === 0) {
                return { year: prev.year - 1, month: 11 };
            }
            return { year: prev.year, month: prev.month - 1 };
        });
    };

    const goToNextMonth = () => {
        setViewMonth(prev => {
            if (prev.month === 11) {
                return { year: prev.year + 1, month: 0 };
            }
            return { year: prev.year, month: prev.month + 1 };
        });
    };

    const goToToday = () => {
        const today = new Date();
        setViewMonth({ year: today.getFullYear(), month: today.getMonth() });
    };

    // Format selected date for display
    const formatSelectedDate = (dateStr: string) => {
        const date = new Date(dateStr + 'T00:00:00');
        const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${dayName}, ${month}-${day}-${year}`;
    };

    // Check if we can navigate to prev/next month
    const canGoPrev = dateRange.min ? (() => {
        const minDate = new Date(dateRange.min + 'T00:00:00');
        return viewMonth.year > minDate.getFullYear() || 
               (viewMonth.year === minDate.getFullYear() && viewMonth.month > minDate.getMonth());
    })() : false;

    const canGoNext = dateRange.max ? (() => {
        const maxDate = new Date(dateRange.max + 'T00:00:00');
        return viewMonth.year < maxDate.getFullYear() || 
               (viewMonth.year === maxDate.getFullYear() && viewMonth.month < maxDate.getMonth());
    })() : false;

    return (
        <div className="datetime-selector">
            {/* Header with availability indicator and timezone */}
            <div className="datetime-selector-header">
                <div className="availability-indicator">
                    <Check size={16} className="check-icon" />
                    <span>{ownerName ? `${ownerName} is available` : 'Available times'}</span>
                </div>
                {showTimezoneSelector && (
                    <div className="timezone-selector">
                        <Globe size={14} />
                        <select
                            value={timezone}
                            onChange={(e) => onTimezoneChange(e.target.value)}
                            className="timezone-select"
                        >
                            {TIMEZONES.map(tz => (
                                <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <div className="datetime-selector-content">
                {/* Calendar Section */}
                <div className="calendar-section">
                    <div className="calendar-header">
                        <span className="calendar-title">
                            {MONTH_NAMES[viewMonth.month]} {viewMonth.year}
                        </span>
                        <div className="calendar-nav">
                            <button
                                onClick={goToPrevMonth}
                                disabled={!canGoPrev}
                                className="calendar-nav-btn"
                                aria-label="Previous month"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <button
                                onClick={goToNextMonth}
                                disabled={!canGoNext}
                                className="calendar-nav-btn"
                                aria-label="Next month"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Day of week headers */}
                    <div className="calendar-weekdays">
                        {DAYS_OF_WEEK.map((day, i) => (
                            <div key={i} className="calendar-weekday">{day}</div>
                        ))}
                    </div>

                    {/* Calendar grid */}
                    <div className="calendar-grid">
                        {calendarDays.map((day, i) => {
                            const isAvailable = availableDatesSet.has(day.date);
                            const isSelected = day.date === selectedDate;
                            const isPast = day.date < new Date().toISOString().split('T')[0];
                            const isDisabled = !isAvailable;

                            return (
                                <button
                                    key={i}
                                    onClick={() => {
                                        if (!isAvailable || isPast) return;
                                        const d = new Date(day.date + 'T00:00:00');
                                        setViewMonth({ year: d.getFullYear(), month: d.getMonth() });
                                        onDateChange(day.date);
                                    }}
                                    disabled={isDisabled || isPast}
                                    className={`calendar-day ${day.isCurrentMonth ? 'current-month' : 'other-month'} ${isAvailable ? 'available' : ''} ${isSelected ? 'selected' : ''} ${day.isToday ? 'today' : ''} ${isPast ? 'past' : ''}`}
                                    aria-label={`${day.date}${isAvailable ? ', available' : ', unavailable'}`}
                                >
                                    {day.day}
                                </button>
                            );
                        })}
                    </div>

                    {/* Today button */}
                    <button onClick={goToToday} className="today-btn">
                        Today
                    </button>
                </div>

                {/* Time Slots Section */}
                <div className="timeslots-section">
                    {selectedDate ? (
                        <>
                            <h3 className="timeslots-title">
                                {formatSelectedDate(selectedDate)}
                            </h3>

                            {slotsForSelectedDate.length > 0 ? (
                                <div className="timeslots-list">
                                    {slotsForSelectedDate.map((slot, idx) => {
                                        const selected = isSlotSelected(slot);
                                        return (
                                            <button
                                                key={idx}
                                                onClick={() => {
                                                    if (selectedDayInfo) {
                                                        onSlotToggle(slot, selectedDate, selectedDayInfo.day_name);
                                                    }
                                                }}
                                                className={`timeslot-item ${selected ? 'selected' : ''}`}
                                            >
                                                <span className="timeslot-time">
                                                    {slot.start_time_local} - {slot.end_time_local}
                                                </span>
                                                <Check size={18} className="timeslot-check" />
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="no-slots">
                                    <Calendar size={32} />
                                    <p>No available times for this date</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="no-date-selected">
                            <Calendar size={48} />
                            <p>Select a date to see available times</p>
                        </div>
                    )}

                    {/* Selection summary for multi-select mode */}
                    {multiSelect && selectedSlots.length > 0 && (
                        <div className="selection-summary">
                            <span>
                                {selectedSlots.length} time{selectedSlots.length !== 1 ? 's' : ''} selected
                                {maxSelections && ` (max ${maxSelections})`}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DateTimeSelector;
